/**
 * SuggestedRoleBundles — Admin reference for scope assignment
 *
 * Shows recommended scope bundles for each BeHeard app role.
 * This is a read-only reference — it does NOT perform Central writes.
 * Admins use this to know which scopes to assign in Central's member management UI.
 */

import { APP_ROLE_PROFILES, SCOPE_BUNDLES } from '../hooks/useAppRole';
import { AppRoleBadge } from './AppRoleBadge';
import type { AppRoleKey } from '../hooks/useAppRole';

const BUNDLE_KEYS: Array<{ profileKey: AppRoleKey; bundleKey: string }> = [
  { profileKey: 'member_standard', bundleKey: 'member_standard' },
  { profileKey: 'member_premium', bundleKey: 'member_premium' },
  { profileKey: 'member_recruiter', bundleKey: 'member_recruiter' },
  { profileKey: 'member_trainer', bundleKey: 'member_trainer' },
  { profileKey: 'member_org_admin', bundleKey: 'member_org_admin' },
  { profileKey: 'admin', bundleKey: 'admin' },
];

export function SuggestedRoleBundles() {
  return (
    <div className="space-y-4">
      {BUNDLE_KEYS.map(({ profileKey, bundleKey }) => {
        const profile = APP_ROLE_PROFILES.find((p) => p.key === profileKey);
        const scopes = SCOPE_BUNDLES[bundleKey];
        if (!profile || !scopes) return null;

        return (
          <div key={profileKey} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AppRoleBadge profileKey={profileKey} size="md" />
              {profile.additive && (
                <span className="text-xs text-gray-400 italic">additive</span>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-3">{profile.description}</p>
            <div className="space-y-1">
              <p className="text-xs text-gray-500 uppercase font-medium">Required Scopes</p>
              <div className="flex flex-wrap gap-1">
                {scopes.map((scope) => (
                  <code
                    key={scope}
                    className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded font-mono"
                  >
                    {scope}
                  </code>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      <p className="text-xs text-gray-400">
        Assign these scopes in Central workspace member settings.
        Owner and Viewer roles do not require explicit scope assignment.
      </p>
    </div>
  );
}
